// conectagran/frontend/src/pages/AdManager/GamSetup.tsx
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from 'sonner'
import { useAuth } from '@/api/hooks/useAuth'
import {
  useGamConnection,
  useGamAdUnits,
  useSaveGamConnection,
  useDiscoverGamAdUnits,
  useLinkGamAdUnit,
} from '@/api/hooks/useGam'
import type { GamAdUnit } from '@/api/hooks/useGam'

interface Props {
  onComplete: () => void
  financePages: { id: string; name: string }[]
}

export function GamSetup({ onComplete, financePages }: Props) {
  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [networkCode, setNetworkCode] = useState('')
  const [links, setLinks] = useState<Record<string, string | null>>({})

  // All hooks must be called unconditionally before any early return (Rules of Hooks)
  const { data: authUser }    = useAuth()
  const { data: connData, isLoading: connLoading } = useGamConnection()
  const { data: adUnitsData } = useGamAdUnits()
  const saveConn              = useSaveGamConnection()
  const discoverUnits         = useDiscoverGamAdUnits()
  const linkUnit              = useLinkGamAdUnit()

  // Guard: while connection query is loading, avoid flash of step-1 UI
  // Placed AFTER all hook calls to comply with Rules of Hooks
  if (connLoading) return null

  // If OAuth already done (connected=true from ?connected=1), advance to step 2
  const alreadyConnected = connData?.connected

  async function handleSaveNetworkCode() {
    if (!networkCode.trim()) {
      toast.error('Informe o Network Code')
      return
    }
    try {
      await saveConn.mutateAsync(networkCode.trim())
      const result = await discoverUnits.mutateAsync()
      if (result.count === 0) {
        toast.error('Nenhum ad unit encontrado nesta rede. Verifique o número e tente novamente.')
        return
      }
      toast.success(`${result.count} ad units encontrados!`)
      setStep(3)
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Erro ao confirmar network code')
    }
  }

  async function handleSaveLinks() {
    const units = adUnitsData?.ad_units ?? []
    try {
      await Promise.all(
        units.map(u =>
          linkUnit.mutateAsync({
            id: u.id,
            financePageId: links[u.id] ?? u.finance_page_id,
          })
        )
      )
      toast.success('Vínculos salvos!')
      onComplete()
    } catch {
      toast.error('Erro ao salvar vínculos')
    }
  }

  // ─── Step 1: OAuth ────────────────────────────────────────────────────────

  if (step === 1 && !alreadyConnected) {
    return (
      <div className="max-w-md mx-auto mt-20 text-center space-y-6">
        <h2 className="text-xl font-bold text-[#f8fafc]">Conectar Google Ad Manager</h2>
        <p className="text-[#94a3b8] text-sm">
          Clique no botão abaixo para autorizar o acesso ao seu Ad Manager via conta Google.
        </p>
        <Button
          className="bg-[#c9a84c] hover:bg-[#9a7b2f] text-black font-semibold px-8"
          onClick={() => {
            // Use authUser?.id from useAuth() — same pattern as YouTube OAuth
            window.location.href = `/oauth/gam/redirect?userId=${encodeURIComponent(authUser?.id ?? '')}`
          }}
        >
          Conectar com Google
        </Button>
      </div>
    )
  }

  // ─── Step 2: Network Code ─────────────────────────────────────────────────

  if (step === 2 || (alreadyConnected && !connData?.network_code)) {
    return (
      <div className="max-w-md mx-auto mt-20 space-y-6">
        <div className="space-y-2">
          <h2 className="text-xl font-bold text-[#f8fafc]">Network Code</h2>
          <p className="text-[#94a3b8] text-sm">
            Conectado como <span className="text-[#c9a84c]">{connData?.google_email}</span>.
          </p>
          <p className="text-[#94a3b8] text-sm">
            Cole o Network Code do seu GAM — é o número na URL:{' '}
            <code className="text-[#e2e8f0] bg-[#1a1d2a] px-1 rounded">
              admanager.google.com/#NÚMERO
            </code>
          </p>
        </div>
        <Input
          placeholder="Ex: 12345678"
          value={networkCode}
          onChange={e => setNetworkCode(e.target.value)}
          className="bg-[#1a1d2a] border-[#252836] text-[#e2e8f0]"
        />
        <Button
          className="w-full bg-[#c9a84c] hover:bg-[#9a7b2f] text-black font-semibold"
          onClick={handleSaveNetworkCode}
          disabled={saveConn.isPending || discoverUnits.isPending}
        >
          {saveConn.isPending || discoverUnits.isPending ? 'Confirmando...' : 'Confirmar'}
        </Button>
      </div>
    )
  }

  // ─── Step 3: Map Ad Units ─────────────────────────────────────────────────

  const units = adUnitsData?.ad_units ?? []

  return (
    <div className="max-w-xl mx-auto mt-16 space-y-6">
      <h2 className="text-xl font-bold text-[#f8fafc]">Mapear Ad Units</h2>
      <p className="text-[#94a3b8] text-sm">
        Vincule cada ad unit a uma página financeira para filtrar os relatórios. Opcional — você pode fazer isso depois.
      </p>

      <div className="space-y-3">
        {units.map(unit => (
          <div key={unit.id} className="flex items-center gap-3 bg-[#1a1d2a] rounded-lg p-3">
            <span className="flex-1 text-sm text-[#e2e8f0] truncate">{unit.ad_unit_name}</span>
            <select
              className="bg-[#252836] border border-[#2e3347] text-[#e2e8f0] rounded px-2 py-1 text-sm"
              value={links[unit.id] ?? unit.finance_page_id ?? ''}
              onChange={e => setLinks(prev => ({ ...prev, [unit.id]: e.target.value || null }))}
            >
              <option value="">— Sem vínculo —</option>
              {financePages.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <Button
          className="flex-1 bg-[#c9a84c] hover:bg-[#9a7b2f] text-black font-semibold"
          onClick={handleSaveLinks}
          disabled={linkUnit.isPending}
        >
          {linkUnit.isPending ? 'Salvando...' : 'Salvar vínculos e abrir dashboard'}
        </Button>
        <Button variant="ghost" className="text-[#64748b]" onClick={onComplete}>
          Pular
        </Button>
      </div>
    </div>
  )
}
